import type { DateRange } from '@/types/SearchBar';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const SATURDAY = 6; // Date.getDay(): 0 = Sunday … 6 = Saturday
const MAX_WEEK_STEPS = 52; // safety cap so the widening loop can never spin forever

/** Strip the time portion, returning a new `Date` at local midnight. */
export function atLocalMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Whole calendar days from `start` to `end`. Both ends are normalized to local
 * midnight and the difference is rounded, so a range crossing a daylight-saving
 * boundary (where the raw millisecond diff is off by an hour) still yields the
 * exact whole-day count. Signed: negative when `end` precedes `start`.
 */
export function daysBetween(start: Date, end: Date): number {
  return Math.round(
    (atLocalMidnight(end).getTime() - atLocalMidnight(start).getTime()) / MS_PER_DAY
  );
}

/** Saturday on or before `date` (returns `date` unchanged if it is already a Saturday). */
function precedingSaturday(date: Date): Date {
  const base = atLocalMidnight(date);
  const back = (base.getDay() - SATURDAY + 7) % 7;
  return addDays(base, -back);
}

/** Saturday on or after `date` (returns `date` unchanged if it is already a Saturday). */
function nextSaturday(date: Date): Date {
  const base = atLocalMidnight(date);
  const forward = (SATURDAY - base.getDay() + 7) % 7;
  return addDays(base, forward);
}

/**
 * Widen a search window to a Saturday-to-Saturday range that can accommodate the
 * selected trip `duration`.
 *
 * Used to recover from a zero-result search: the start is moved to the preceding
 * Saturday and the end to the next Saturday, then the end is pushed forward by
 * further weeks until the window spans at least `duration` days. `duration` itself
 * is unchanged — only the flexibility window grows.
 *
 * The start is clamped to the first bookable Saturday (the Saturday on or after
 * `minDate`): when the user picks a date close to today, the preceding Saturday
 * would land in the past and the booking validation rejects it. In that case the
 * whole window is shifted forward to start at the first future Saturday and the
 * widening loop grows the end from there.
 *
 * `minDate` defaults to today at local midnight, matching the "not in the past"
 * booking validation; it is injectable for testing.
 *
 * Returns `null` if the input range is incomplete.
 */
export function extendRangeToSaturdayWindow(
  range: DateRange,
  duration: number,
  minDate: Date = atLocalMidnight(new Date())
): DateRange {
  if (!range || !(range[0] instanceof Date) || !(range[1] instanceof Date)) {
    return null;
  }

  const earliestStart = nextSaturday(minDate);
  let start = precedingSaturday(range[0]);
  if (start < earliestStart) {
    start = earliestStart;
  }

  let end = nextSaturday(range[1]);
  let steps = 0;
  while (daysBetween(start, end) < duration && steps < MAX_WEEK_STEPS) {
    end = addDays(end, 7);
    steps++;
  }

  return [start, end];
}
